import { isDate } from 'lodash'
import {
  CreateParams,
  CreateResult,
  DataProvider,
  DeleteParams,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
} from 'react-admin'
import { SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL } from '../../../api-urls'
import { ignoreTimezoneOffset } from '../../../common/date-time'
import {
  get,
  getWithPagination,
  patch,
  post,
  remove,
  put,
} from '../../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../../common/ticket-api-get-list.params'
import mapSortParam from './selfpos-access-ticket-cashless-machine-sort.mapper'
import filterMapper from './selfpos-access-ticket-cashless-machine-filter.mapper'
import { SelfPOSAccessTicketCashlessMachineDto } from '../../../dto/device/ticket-machines/selfpos-access-ticket-cashless-machine/selfpos-access-ticket-cashless-machine.dto'
import { SetCredentialsDto } from '../../../dto/device/ticket-machines/automat-api/set-credentials.dto'
import { CacheableDataProviderExtensionResult } from '../../../common/data-provider'
import { RemoveCredentialsDto } from '../../../dto/device/ticket-machines/automat-api/remove-credentials.dto'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<SelfPOSAccessTicketCashlessMachineDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(sort, pagination, mapSortParam)
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<SelfPOSAccessTicketCashlessMachineDto[]>(
      `${SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<SelfPOSAccessTicketCashlessMachineDto>> => {
    const data = await get<SelfPOSAccessTicketCashlessMachineDto>(
      SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL,
      `/${id}`,
    )
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<SelfPOSAccessTicketCashlessMachineDto>> => {
    const data = await get<SelfPOSAccessTicketCashlessMachineDto[]>(
      SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL,
      `/GetByConditions/e=>new int[] {${ids.toString()}}.Contains(e.Id)`,
    )
    return Promise.resolve({ data })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateSelfPOSAccessTicketCashlessMachineRequest>,
  ): Promise<CreateResult<SelfPOSAccessTicketCashlessMachineDto>> => {
    const tscan = await post<
      CreateSelfPOSAccessTicketCashlessMachineRequest[],
      SelfPOSAccessTicketCashlessMachineDto[]
    >(SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL, [data])
    return {
      data: tscan[0],
    }
  },
  update: async (
    resource: string,
    {
      id,
      data,
      previousData,
    }: UpdateParams<SelfPOSAccessTicketCashlessMachineDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      SelfPOSAccessTicketCashlessMachineDto
    >(SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL, {
      ...Object.fromEntries(
        Object.entries(data)?.map(([key, value]) => {
          if (value !== previousData?.[key]) {
            if (value && isDate(value)) {
              return [key, ignoreTimezoneOffset(value).toISOString()]
            }
            return [key, value]
          }
          return [key, undefined]
        }),
      ),
      id,
    })
    return Promise.resolve({ data: update })
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<CreateResult> => {
    const deleted = await remove<SelfPOSAccessTicketCashlessMachineDto>(
      SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL,
      `/${id}`,
    )
    return Promise.resolve({ data: deleted })
  },
  restart: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const reseted = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL}/Restart/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: reseted })
  },
  lock: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const locked = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL}/Lock/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: locked })
  },
  unlock: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const unlocked = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL}/Unlock/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: unlocked })
  },
  rebootDevice: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const rebooted = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL}/RebootDevice/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: rebooted })
  },
  shutDownDevice: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const shutDown = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL}/ShutDownDevice/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: shutDown })
  },
  printTestTicket: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const printed = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL}/PrintTestTicket`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: printed })
  },
  setAutomatAPICredentials: async (
    resource: string,
    params: SetCredentialsDto,
  ): Promise<CacheableDataProviderExtensionResult<SetCredentialsDto>> => {
    const response = await put<SetCredentialsDto, SetCredentialsDto>(
      `${SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL}/SetAutomatAPICredentials`,
      params,
    )
    return {
      data: response,
    }
  },
  removeAutomatAPICredentials: async (
    resource: string,
    params: RemoveCredentialsDto,
  ): Promise<CacheableDataProviderExtensionResult<RemoveCredentialsDto>> => {
    const response = await put<RemoveCredentialsDto, RemoveCredentialsDto>(
      `${SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL}/RemoveAutomatAPICredentials`,
      params,
    )
    return {
      data: response,
    }
  },
  getAnnouncements: async (
    resource: string,
    params: TicketMachineIdParams,
  ): Promise<
    CacheableDataProviderExtensionResult<TicketMachineAnnouncementsDto>
  > => {
    const data = await get<TicketMachineAnnouncementsDto>(
      SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL,
      `/GetAnnouncements/${params.ticketMachineId}`,
    )
    return Promise.resolve({
      data,
    })
  },
  setAnnouncements: async (
    resource: string,
    params: TicketMachineAnnouncementsDto,
  ): Promise<
    CacheableDataProviderExtensionResult<TicketMachineAnnouncementsDto>
  > => {
    const response = await patch<
      TicketMachineAnnouncementsDto,
      TicketMachineAnnouncementsDto
    >(`${SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_URL}/SetAnnouncements`, params)
    return {
      data: response,
    }
  },
} as SelfPOSAccessTicketCashlessMachinesProvider

interface SelfPOSAccessTicketCashlessMachinesProvider extends DataProvider {
  restart: (resource: string, params: TicketMachineIdParams) => Promise<any>
  lock: (resource: string, params: TicketMachineIdParams) => Promise<any>
  unlock: (resource: string, params: TicketMachineIdParams) => Promise<any>
  rebootDevice: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<any>
  shutDownDevice: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<any>
  printTestTicket: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<any>

  setAutomatAPICredentials: (
    resource: string,
    params: SetCredentialsDto,
  ) => Promise<CacheableDataProviderExtensionResult<SetCredentialsDto>>
  removeAutomatAPICredentials: (
    resource: string,
    params: RemoveCredentialsDto,
  ) => Promise<CacheableDataProviderExtensionResult<RemoveCredentialsDto>>

  getAnnouncements: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<
    CacheableDataProviderExtensionResult<TicketMachineAnnouncementsDto>
  >
  setAnnouncements: (
    resource: string,
    params: TicketMachineAnnouncementsDto,
  ) => Promise<
    CacheableDataProviderExtensionResult<TicketMachineAnnouncementsDto>
  >
}

interface CreateSelfPOSAccessTicketCashlessMachineRequest {
  readonly name: string
  readonly isWatched: string
  readonly categoryId: number
  readonly cameraId: number
  readonly address: string
}

interface TicketMachineIdParams {
  readonly ticketMachineId: number
}

interface TicketMachineIdRequest {
  readonly ticketMachineId: Identifier
}

interface TicketMachineAnnouncementsDto {
  readonly ticketMachineId: Identifier
  readonly announcements: AnnouncementsDto
}

interface AnnouncementsDto {
  readonly eventsListText?: string | null
  readonly variantsListText?: string | null
  readonly cartText?: string | null
}

export default provider
